import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  User, Calendar, Clock, CheckCircle, AlertCircle, Package, 
  LogOut, ChevronRight, MessageCircle, Mail, MapPin, FileText, Loader2
} from 'lucide-react';
import { useClientAuth } from '../context/ClientAuthContext';
import { useLanguage } from '../context/LanguageContext';

const ClientDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  const { client, token, logout, isAuthenticated } = useClientAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Get region from URL
  const getBasePath = () => {
    if (location.pathname.startsWith('/ie')) return '/ie';
    if (location.pathname.startsWith('/pt')) return '/pt';
    return '/pt';
  };
  const basePath = getBasePath();

  const getText = (en, pt) => language === 'pt' ? pt : en;

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`${basePath}/client/login`);
    }
  }, [isAuthenticated, navigate, basePath]);

  // Fetch orders
  useEffect(() => {
    const fetchOrders = async () => {
      if (!token) return;
      
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/client/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch orders');
        }

        const data = await response.json();
        setOrders(data.orders || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate(basePath);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    const statuses = {
      pending: getText('Pending', 'Pendente'),
      in_progress: getText('In Progress', 'Em Progresso'),
      completed: getText('Completed', 'Concluído')
    };
    return statuses[status] || status;
  };

  const getTypeText = (type) => {
    return type === 'quote' 
      ? getText('Quote Request', 'Pedido de Orçamento')
      : getText('Booking', 'Agendamento');
  };

  const filteredOrders = orders.filter(order => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return order.status === 'pending';
    if (activeTab === 'confirmed') return order.status === 'in_progress' || order.status === 'completed';
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-IE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-4" data-testid="client-dashboard">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-[#1a5c3a] to-[#0f3d26] rounded-2xl p-8 mb-8 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-white/70 text-sm mb-1">{getText('Client Area', 'Área do Cliente')}</p>
              <h1 className="text-2xl font-heading font-bold mb-2">
                {getText(`Hello, ${client?.name}!`, `Olá, ${client?.name}!`)}
              </h1>
              <p className="text-white/80">
                {getText('Track your quotes and services here.', 'Acompanhe os seus orçamentos e serviços aqui.')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link 
                to="/ie/quote"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#1a5c3a] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                <FileText size={18} />
                {getText('Request New Quote', 'Pedir Novo Orçamento')}
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                data-testid="logout-btn"
              >
                <LogOut size={18} />
                <span className="hidden sm:inline">{getText('Logout', 'Sair')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <Package className="text-[#1a5c3a] mb-2" size={24} />
            <p className="text-2xl font-bold text-gray-900">{orders.length}</p>
            <p className="text-sm text-gray-500">{getText('Total Quotes', 'Total de Orçamentos')}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <Clock className="text-yellow-500 mb-2" size={24} />
            <p className="text-2xl font-bold text-gray-900">
              {orders.filter(o => o.status === 'pending').length}
            </p>
            <p className="text-sm text-gray-500">{getText('Awaiting Response', 'Aguardando Resposta')}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <AlertCircle className="text-blue-500 mb-2" size={24} />
            <p className="text-2xl font-bold text-gray-900">
              {orders.filter(o => o.status === 'in_progress').length}
            </p>
            <p className="text-sm text-gray-500">{getText('Confirmed', 'Confirmados')}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <CheckCircle className="text-green-500 mb-2" size={24} />
            <p className="text-2xl font-bold text-gray-900">
              {orders.filter(o => o.status === 'completed').length}
            </p>
            <p className="text-sm text-gray-500">{getText('Completed', 'Concluídos')}</p>
          </div>
        </div>

        {/* Orders Section */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="border-b p-4">
            <h2 className="text-lg font-heading font-bold text-gray-900 mb-4">
              {getText('Your Quotes & Services', 'Os Seus Orçamentos e Serviços')}
            </h2>
            <div className="flex gap-2">
              {['all', 'pending', 'confirmed'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === tab
                      ? 'bg-[#1a5c3a] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`tab-${tab}`}
                >
                  {tab === 'all' && getText('All', 'Todos')}
                  {tab === 'pending' && getText('Awaiting', 'Pendentes')}
                  {tab === 'confirmed' && getText('Confirmed', 'Confirmados')}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#1a5c3a] mx-auto mb-4" />
              <p className="text-gray-500">{getText('Loading...', 'A carregar...')}</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center text-red-500">
              <AlertCircle className="w-8 h-8 mx-auto mb-4" />
              <p>{error}</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">
                {getText('No quotes found', 'Nenhum orçamento encontrado')}
              </p>
              <Link
                to="/ie/quote"
                className="inline-flex items-center gap-2 text-[#1a5c3a] font-medium hover:underline"
              >
                {getText('Request your first quote', 'Peça o seu primeiro orçamento')}
                <ChevronRight size={16} />
              </Link>
            </div>
          ) : (
            <div className="divide-y">
              {filteredOrders.map((order) => (
                <div key={order.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {getStatusText(order.status)}
                        </span>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {getTypeText(order.type)}
                        </span>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {order.service_type}
                      </h3>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {formatDate(order.timestamp || order.created_at)}
                        </span>
                        {order.address && (
                          <span className="flex items-center gap-1">
                            <MapPin size={14} />
                            {order.address.substring(0, 30)}...
                          </span>
                        )}
                        {order.preferred_date && (
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {order.preferred_date} {order.preferred_time}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="text-gray-400 flex-shrink-0" size={20} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contact Info */}
        <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm">
          <h3 className="font-heading font-bold text-gray-900 mb-4">
            {getText('Need Help?', 'Precisa de Ajuda?')}
          </h3>
          <div className="flex flex-wrap gap-6">
            <a href="https://wa.me/353899502499" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#25D366] hover:text-[#20BA5A]">
              <MessageCircle size={20} />
              <span>WhatsApp: 089 950 2499</span>
            </a>
            <a href="mailto:suporte@sancurservices.com" className="flex items-center gap-2 text-gray-600 hover:text-[#1a5c3a]">
              <Mail size={20} />
              <span>suporte@sancurservices.com</span>
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClientDashboard;
